import { useRef, useState } from 'react';
import type { GridApi, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import AGDataGrid from '@/components/ag-data-grid';
import Container from '@/components/container';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useGrid } from '@/contexts/grid-context';
import {
  useRegulatoryLinks,
  useCreateRegulatoryLink,
  useUpdateRegulatoryLink,
  useDeleteRegulatoryLinks,
} from '@/hooks/use-regulatory-links';
import type { RegulatoryLink, CreateRegulatoryLinkInput, UpdateRegulatoryLinkInput } from '@/types/regulatory-link';
import { databaseColumnDefs } from './components/database-grid-columns';
import DatabaseSheet from './components/database-sheet';

function DatabasesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<RegulatoryLink[]>([]);
  const [editingLink, setEditingLink] = useState<RegulatoryLink | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const gridApiRef = useRef<GridApi<RegulatoryLink> | null>(null);
  const { registerGridApi } = useGrid();

  const { data: regulatoryLinks, isLoading } = useRegulatoryLinks();
  const createMutation = useCreateRegulatoryLink();
  const updateMutation = useUpdateRegulatoryLink();
  const deleteMutation = useDeleteRegulatoryLinks();

  const handleCreateSubmit = async (data: CreateRegulatoryLinkInput) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success('Database created successfully');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  };

  const handleUpdateSubmit = async (data: UpdateRegulatoryLinkInput) => {
    if (!editingLink)
      return;

    try {
      await updateMutation.mutateAsync({ id: editingLink.id, ...data });
      toast.success('Database updated successfully');
      setEditingLink(null);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
      throw error;
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0)
      return;

    try {
      await deleteMutation.mutateAsync(selectedRows.map((row) => row.id));
      toast.success('Selected databases deleted successfully');
      setSelectedRows([]);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  };

  const handleRowDoubleClick = (event: RowDoubleClickedEvent<RegulatoryLink>) => {
    if (event.data) {
      setEditingLink(event.data);
      setIsSheetOpen(true);
    }
  };

  const handleSelectionChanged = (event: { api: { getSelectedRows: () => RegulatoryLink[] } }) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setEditingLink(null);
      // Deselect rows when closing the sheet
      if (gridApiRef.current) {
        gridApiRef.current.deselectAll();
      }
    }
  };

  const handleGridReady = (params: { api: GridApi<RegulatoryLink> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  const clearFilters = () => {
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
      setSearchQuery('');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    // Stop the click from bubbling up to the app layout
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-3 flex justify-between">
        <div>
          <h1 className="mt-2 text-xl font-bold">Databases</h1>
        </div>
        <div className="flex items-center gap-4">
          {selectedRows.length > 0 && (
            <Button
              variant="destructive"
              onClick={handleDeleteClick}
              disabled={deleteMutation.isPending}
            >
              Delete Selected
            </Button>
          )}
          <Button onClick={() => setIsSheetOpen(true)}>
            Create Database
          </Button>
        </div>
      </div>
      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search databases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={clearFilters}
          className="gap-2"
        >
          <RotateCcw className="size-4" />
          Clear Filters
        </Button>
      </div>
      <div className={isLoading ? 'opacity-50' : ''}>
        <AGDataGrid
          columnDefs={databaseColumnDefs}
          rowData={regulatoryLinks || []}
          quickFilterText={searchQuery}
          rowSelection={{ mode: 'multiRow' }}
          onRowDoubleClicked={handleRowDoubleClick}
          onSelectionChanged={handleSelectionChanged}
          onGridReady={handleGridReady}
          overlayLoadingTemplate={
            isLoading ? '<span class="ag-overlay-loading-center">Loading...</span>' : undefined
          }
          overlayNoRowsTemplate={
            isLoading ? '' : '<span class="ag-overlay-no-rows-center">No databases found</span>'
          }
        />
      </div>

      <DatabaseSheet
        open={isSheetOpen}
        onOpenChange={handleSheetOpenChange}
        initialData={editingLink || undefined}
        onSubmit={editingLink ? handleUpdateSubmit : handleCreateSubmit}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete
              {' '}
              {selectedRows.length}
              {' '}
              selected
              {' '}
              {selectedRows.length === 1 ? 'database' : 'databases'}
              .
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Container>
  );
}

export default DatabasesPage;
